import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Search, UserCheck, Calendar, X } from "lucide-react";
import { AttendanceRecord, AttendanceFilter } from "../../types/attendance";
import { getAttendanceRecords } from "../../services/attendance";
import AttendanceCalendarModal from "../modals/AttendanceCalendarModal";

interface AttendanceTableProps {
  records: AttendanceRecord[];
  onRefresh: () => void;
  filters: AttendanceFilter;
  onFiltersChange: (filters: AttendanceFilter) => void;
}

interface StudentSummary {
  userId: string;
  userName: string;
  userType: string;
  studentId?: string;
  daysAttended: number;
  lastAttendance?: Date;
}

const AttendanceTable: React.FC<AttendanceTableProps> = ({
  records,
  onRefresh,
  filters,
  onFiltersChange,
}) => {
  const [viewingUser, setViewingUser] = useState<{
    userId: string;
    userName: string;
    userType: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Separate query for search — fetches ALL records without date filter
  // Only fires when the user has typed something; never affects the parent's filters
  const { data: allRecordsData } = useQuery({
    queryKey: ["attendance-search-all", filters.userType],
    queryFn: () =>
      getAttendanceRecords({ userType: filters.userType, limit: 2000 }),
    enabled: !!searchQuery.trim(),
    staleTime: 2 * 60 * 1000, // cache for 2 min to avoid re-fetching on every keystroke
  });

  const allRecords: AttendanceRecord[] = allRecordsData?.data ?? [];

  // Group records by user and calculate summary
  const studentSummaries = useMemo(() => {
    const userMap = new Map<string, StudentSummary>();

    records.forEach((record) => {
      const userId =
        typeof record.userId === "string"
          ? record.userId
          : (record.userId as any)?._id || "";

      const userName =
        typeof record.userId === "object" && record.userId !== null
          ? `${(record.userId as any).firstName || ""} ${
              (record.userId as any).lastName || ""
            }`.trim() || "Unknown"
          : "Unknown";

      const studentId =
        typeof record.userId === "object" && (record.userId as any)?.studentId
          ? (record.userId as any).studentId
          : undefined;

      if (!userMap.has(userId)) {
        userMap.set(userId, {
          userId,
          userName,
          userType: record.userType,
          studentId,
          daysAttended: 0,
          lastAttendance: undefined,
        });
      }

      const summary = userMap.get(userId)!;
      summary.daysAttended++;

      const recordDate = new Date(record.date);
      if (!summary.lastAttendance || recordDate > summary.lastAttendance) {
        summary.lastAttendance = recordDate;
      }
    });

    return Array.from(userMap.values()).sort((a, b) =>
      a.userName.localeCompare(b.userName),
    );
  }, [records]);

  // Build summaries from ALL records (used only during search)
  const allStudentSummaries = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const userMap = new Map<string, StudentSummary>();
    allRecords.forEach((record) => {
      const userId =
        typeof record.userId === "string"
          ? record.userId
          : (record.userId as any)?._id || "";
      const userName =
        typeof record.userId === "object" && record.userId !== null
          ? `${(record.userId as any).firstName || ""} ${
              (record.userId as any).lastName || ""
            }`.trim() || "Unknown"
          : "Unknown";
      const studentId =
        typeof record.userId === "object" && (record.userId as any)?.studentId
          ? (record.userId as any).studentId
          : undefined;
      if (!userMap.has(userId)) {
        userMap.set(userId, {
          userId,
          userName,
          userType: record.userType,
          studentId,
          daysAttended: 0,
          lastAttendance: undefined,
        });
      }
      const summary = userMap.get(userId)!;
      summary.daysAttended++;
      const recordDate = new Date(record.date);
      if (!summary.lastAttendance || recordDate > summary.lastAttendance) {
        summary.lastAttendance = recordDate;
      }
    });
    return Array.from(userMap.values()).sort((a, b) =>
      a.userName.localeCompare(b.userName),
    );
  }, [allRecords, searchQuery]);

  // Filter students based on search
  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return studentSummaries;

    // Search across ALL records (not just the date-filtered ones)
    const query = searchQuery.toLowerCase();
    return allStudentSummaries.filter(
      (student) =>
        student.userName.toLowerCase().includes(query) ||
        student.studentId?.toLowerCase().includes(query) ||
        student.userId.toLowerCase().includes(query),
    );
  }, [studentSummaries, allStudentSummaries, searchQuery]);

  const handleView = (student: StudentSummary) => {
    setViewingUser({
      userId: student.userId,
      userName: student.userName,
      userType: student.userType,
    });
  };

  // Calculate total possible days from date range
  const getTotalDays = () => {
    if (!filters.dateFrom || !filters.dateTo) return null;
    const start = new Date(filters.dateFrom);
    const end = new Date(filters.dateTo);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    // Count only weekdays
    let weekdays = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) weekdays++; // Exclude Sunday (0) and Saturday (6)
    }
    return weekdays;
  };

  const totalDays = getTotalDays();

  const getAttendanceRate = (attended: number) => {
    if (!totalDays || totalDays === 0) return null;
    return ((attended / totalDays) * 100).toFixed(1);
  };

  const getAttendanceColor = (attended: number) => {
    const rate = totalDays ? (attended / totalDays) * 100 : 0;
    if (rate >= 80) return "text-green-600 bg-green-50 border-green-200";
    if (rate >= 60) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  // Calculate present today (records from today)
  const getPresentToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return records.filter((record) => {
      const recordDate = new Date(record.date);
      recordDate.setHours(0, 0, 0, 0);
      return recordDate.getTime() === today.getTime();
    }).length;
  };

  const presentToday = getPresentToday();

  return (
    <div className="space-y-4">
      {/* Present Today Metric */}
      {presentToday > 0 && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl p-3 shadow-md">
                <UserCheck className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-green-700">
                  Present Today
                </p>
                <p className="text-3xl font-bold text-green-900">
                  {presentToday}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-medium text-green-600">
                {new Date().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name or admission number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-sm text-slate-500 mt-2">
            {filteredStudents.length === 0
              ? "No students matched — searching across all records"
              : `Found ${filteredStudents.length} student${filteredStudents.length !== 1 ? "s" : ""}`}
          </p>
        )}
      </div>

      {/* Students List */}
      {filteredStudents.length > 0 ? (
        <div className="space-y-3">
          {filteredStudents.map((student) => {
            const rate = getAttendanceRate(student.daysAttended);
            const colorClass = getAttendanceColor(student.daysAttended);

            return (
              <div
                key={student.userId}
                className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Student Info */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="avatar placeholder">
                      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-md">
                        <span className="text-sm font-bold">
                          {student.userName.slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 truncate">
                        {student.userName}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {student.studentId && (
                          <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                            {student.studentId}
                          </span>
                        )}
                        <span className="text-xs font-medium text-slate-500 capitalize">
                          {student.userType}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Attendance Stats */}
                  <div className="flex items-center gap-3">
                    <div
                      className={`rounded-lg border px-3 py-2 ${colorClass}`}
                    >
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4" />
                        <div className="text-left">
                          <p className="text-xs font-medium opacity-80">
                            Attendance
                          </p>
                          <p className="text-sm font-bold">
                            {student.daysAttended}
                            {totalDays && (
                              <span className="font-normal">/{totalDays}</span>
                            )}
                            {rate && (
                              <span className="text-xs ml-1">({rate}%)</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* View Button */}
                    <button
                      onClick={() => handleView(student)}
                      className="btn btn-sm bg-blue-500 hover:bg-blue-600 text-white border-none shadow-sm hover:shadow-md transition-all"
                      title="View Calendar"
                    >
                      <Eye className="h-4 w-4" />
                      <span className="hidden sm:inline ml-1">View</span>
                    </button>
                  </div>
                </div>

                {/* Last Attendance */}
                {student.lastAttendance && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>
                        Last attendance:{" "}
                        <span className="font-medium">
                          {student.lastAttendance.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="inline-block p-4 bg-slate-100 rounded-full mb-4">
            <Search className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">
            {searchQuery ? "No students found" : "No attendance records"}
          </h3>
          <p className="text-slate-600">
            {searchQuery
              ? "Try adjusting your search query"
              : "No attendance data available for the selected date range"}
          </p>
        </div>
      )}

      {/* Summary Stats */}
      {filteredStudents.length > 0 && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-900">
                {filteredStudents.length}
              </p>
              <p className="text-xs font-medium text-blue-700 mt-1">
                Total Students
              </p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-900">
                {Math.round(
                  filteredStudents.reduce((sum, s) => sum + s.daysAttended, 0) /
                    filteredStudents.length,
                )}
              </p>
              <p className="text-xs font-medium text-blue-700 mt-1">
                Avg Days Attended
              </p>
            </div>
            <div className="text-center col-span-2 sm:col-span-1">
              <p className="text-2xl font-bold text-blue-900">
                {totalDays
                  ? (
                      (filteredStudents.reduce(
                        (sum, s) => sum + s.daysAttended,
                        0,
                      ) /
                        (filteredStudents.length * totalDays)) *
                      100
                    ).toFixed(1)
                  : "N/A"}
                {totalDays && "%"}
              </p>
              <p className="text-xs font-medium text-blue-700 mt-1">
                Overall Rate
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Calendar Modal */}
      {viewingUser && (
        <AttendanceCalendarModal
          userId={viewingUser.userId}
          userName={viewingUser.userName}
          userType={viewingUser.userType}
          onClose={() => setViewingUser(null)}
        />
      )}
    </div>
  );
};

export default AttendanceTable;
